// The normalized data model shared by server (adapter) and client (rendering
// + live evaluation). The raw Prague ZPS source is messy; everything downstream
// speaks THIS vocabulary instead.

/**
 * @typedef {Object} TariffRule
 * A single weekly-recurring window during which the section is PAID.
 * @property {number[]} days   ISO weekdays the window applies to (1=Mon..7=Sun)
 * @property {string}   start  "HH:MM" start of paid window (inclusive)
 * @property {string}   end    "HH:MM" end of paid window (exclusive); "24:00" = midnight
 * @property {number} [price]  CZK per hour, if known
 * @property {number} [maxHours] max continuous paid stay in hours, if known
 */

/**
 * @typedef {Object} SectionProps
 * @property {string}  id            Section code, e.g. "P1-0257"
 * @property {string}  district      e.g. "Praha 1"
 * @property {('resident'|'mixed'|'visitor'|'other')} category
 * @property {string}  color         hex color derived from category
 * @property {TariffRule[]} rules        paid windows on normal days (empty => never charged)
 * @property {TariffRule[]} holidayRules explicit paid windows on public holidays;
 *   empty => the NORMAL rules of that weekday apply (Prague's official regime
 *   since 1 May 2024 — holidays are charged, they are not Sundays)
 * @property {string} [paymentUrl]   deep link / info for paying
 * @property {string} [rawHours]     original human-readable operating hours
 * @property {string} [zoneCode]     original raw zone code from source
 */

export const CATEGORY = {
  RESIDENT: 'resident',
  MIXED: 'mixed',
  VISITOR: 'visitor',
  OTHER: 'other',
}

// Colors chosen to echo Prague's official signage: blue = resident,
// purple/violet = mixed, orange = visitor.
export const CATEGORY_COLOR = {
  resident: '#2f6fd8',
  mixed: '#8e44ad',
  visitor: '#e67e22',
  other: '#7f8c8d',
}

export const CATEGORY_LABEL_CS = {
  resident: 'Rezidentní (modrá)',
  mixed: 'Smíšená (fialová)',
  visitor: 'Návštěvnická (oranžová)',
  other: 'Ostatní',
}

export const CATEGORY_LABEL_EN = {
  resident: 'Resident (blue)',
  mixed: 'Mixed (purple)',
  visitor: 'Visitor (orange)',
  other: 'Other',
}

// Status colors used when coloring the map by free/paid-right-now.
export const STATUS_COLOR = {
  free: '#2ecc71', // green — you can park for free right now
  soon: '#f5a623', // amber — paid now, but flips free within 30 minutes
  paid: '#e74c3c', // red — paid right now
  unknown: '#95a5a6',
}

// The official online parking meter ("Parkování Praha") pays per section code,
// e.g. https://platba.parkujvpraze.cz/buy/pz/P6-1752 . The old ke-utc.appspot.com
// deep links are dead (404), so build the current one from the section id.
export const PAYMENT_BASE = 'https://platba.parkujvpraze.cz/buy/pz'

/** Official pay-for-parking deep link for a section code. */
export function paymentUrlFor(id) {
  return `${PAYMENT_BASE}/${encodeURIComponent(id)}`
}

/** Map a raw Czech category/color string to a normalized category. */
export function normalizeCategory(raw) {
  if (!raw) return CATEGORY.OTHER
  const s = String(raw).toLowerCase()
  if (s.includes('reziden') || s.includes('modr') || s.includes('blue')) return CATEGORY.RESIDENT
  if (s.includes('smíšen') || s.includes('smisen') || s.includes('fialov') || s.includes('purple') || s.includes('violet'))
    return CATEGORY.MIXED
  if (s.includes('návštěv') || s.includes('navstev') || s.includes('oranž') || s.includes('oranz') || s.includes('orange'))
    return CATEGORY.VISITOR
  return CATEGORY.OTHER
}
